<script setup>
import { onMounted, onBeforeUnmount, computed, ref, reactive, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { directive as vOnClickaway } from 'vue3-click-away';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useUISettings } from 'dashboard/composables/useUISettings';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';

import ContactsListLayout from 'dashboard/components-next/Contacts/ContactsListLayout.vue';
import SzContactsEmpty from 'dashboard/components-next/Contacts/EmptyState/SzContactsEmpty.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactsList from 'dashboard/components-next/Contacts/Pages/ContactsList.vue';
import ContactsBulkActionBar from '../components/ContactsBulkActionBar.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import BulkActionsAPI from 'dashboard/api/bulkActions';
// SZ: filter sheet de CONTATOS — versão adaptada (etiquetas + bloqueados + período).
// Mesmo visual do Figma mas só com campos que fazem sentido pra contato.
import SzContactsFilterSheet from 'dashboard/components-next/Contacts/ContactsHeader/SzContactsFilterSheet.vue';

const DEFAULT_SORT_FIELD = 'last_activity_at';
const DEBOUNCE_DELAY = 300;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { updateUISettings, uiSettings } = useUISettings();

const contacts = useMapGetter('contacts/getContactsList');
const uiFlags = useMapGetter('contacts/getUIFlags');
const customViewsUiFlags = useMapGetter('customViews/getUIFlags');
const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const meta = useMapGetter('contacts/getMeta');

const searchQuery = computed(() => route.query?.search);
const searchValue = ref(searchQuery.value || '');
const pageNumber = computed(() => Number(route.query?.page) || 1);

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { contacts_sort_by: contactSortBy = '' } = uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(contactSortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeLabel = computed(() => route.params.label);
const activeSegmentId = computed(() => route.params.segmentId);
const isFetchingList = computed(
  () => uiFlags.value.isFetching || customViewsUiFlags.value.isFetching
);
const currentPage = computed(() => Number(meta.value?.currentPage));
const totalItems = computed(() => meta.value?.count);

// SZ: infinite scroll — controla o sentinel, loadMore e estado de "fetching more"
const PER_PAGE = 15;
const totalPages = computed(() => Math.ceil((totalItems.value || 0) / PER_PAGE) || 1);
const hasMorePages = computed(() => currentPage.value < totalPages.value);
const isFetchingMore = computed(() => Boolean(uiFlags.value?.isFetchingMore));
const infiniteScrollSentinel = ref(null);
let _ioObserver = null;
const loadMore = async () => {
  if (isFetchingMore.value) return;
  if (!hasMorePages.value) return;
  const next = (currentPage.value || 1) + 1;
  try {
    await store.dispatch('contacts/getMore', {
      page: next,
      sortAttr: buildSortAttr(),
      label: activeLabel.value,
    });
  } catch (e) { /* swallow */ }
};

const selectedContactIds = ref([]);
const isBulkActionLoading = ref(false);
const bulkDeleteDialogRef = ref(null);
const selectedCount = computed(() => selectedContactIds.value.length);
const bulkDeleteDialogTitle = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.TITLE')
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_TITLE')
);
const bulkDeleteDialogDescription = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.DESCRIPTION', {
        count: selectedCount.value,
      })
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_DESCRIPTION')
);
const bulkDeleteDialogConfirmLabel = computed(() =>
  selectedCount.value > 1
    ? t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_MULTIPLE')
    : t('CONTACTS_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_SINGLE')
);
const hasSelection = computed(() => selectedCount.value > 0);
const activeSegment = computed(() => {
  if (!activeSegmentId.value) return undefined;
  return segments.value.find(view => view.id === Number(activeSegmentId.value));
});

const hasContacts = computed(() => contacts.value.length > 0);
const isContactIndexView = computed(
  () => route.name === 'contacts_dashboard_index' && pageNumber.value === 1
);
const isActiveView = computed(() => route.name === 'contacts_dashboard_active');
const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length > 0;
});

const showEmptyStateLayout = computed(() => {
  return (
    !searchQuery.value &&
    !hasContacts.value &&
    isContactIndexView.value &&
    !hasAppliedFilters.value
  );
});
const showEmptyText = computed(() => {
  return (
    (searchQuery.value ||
      hasAppliedFilters.value ||
      !isContactIndexView.value) &&
    !hasContacts.value
  );
});

const headerTitle = computed(() => {
  if (searchQuery.value) return t('CONTACTS_LAYOUT.HEADER.SEARCH_TITLE');
  if (isActiveView.value) return t('CONTACTS_LAYOUT.HEADER.ACTIVE_TITLE');
  if (activeSegmentId.value) return activeSegment.value?.name;
  if (activeLabel.value) return `#${activeLabel.value}`;
  return t('CONTACTS_LAYOUT.HEADER.TITLE');
});

const emptyStateMessage = computed(() => {
  if (isActiveView.value)
    return t('CONTACTS_LAYOUT.EMPTY_STATE.ACTIVE_EMPTY_STATE_TITLE');
  if (!searchQuery.value || hasAppliedFilters.value)
    return t('CONTACTS_LAYOUT.EMPTY_STATE.LIST_EMPTY_STATE_TITLE');
  return t('CONTACTS_LAYOUT.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE');
});

const visibleContactIds = computed(() =>
  contacts.value.map(contact => contact.id)
);

const clearSelection = () => {
  selectedContactIds.value = [];
};

const openBulkDeleteDialog = () => {
  if (!selectedContactIds.value.length || isBulkActionLoading.value) return;
  bulkDeleteDialogRef.value?.open?.();
};

const toggleSelectAll = shouldSelect => {
  selectedContactIds.value = shouldSelect ? [...visibleContactIds.value] : [];
};

const toggleContactSelection = ({ id, value }) => {
  const isAlreadySelected = selectedContactIds.value.includes(id);
  const shouldSelect = value ?? !isAlreadySelected;

  if (shouldSelect && !isAlreadySelected) {
    selectedContactIds.value = [...selectedContactIds.value, id];
  } else if (!shouldSelect && isAlreadySelected) {
    selectedContactIds.value = selectedContactIds.value.filter(
      contactId => contactId !== id
    );
  }
};

const updatePageParam = (page, search = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
    ...(search ? { search } : {}),
  };

  if (!search) {
    delete query.search;
  }

  router.replace({ query });
};

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const getCommonFetchParams = (page = 1) => ({
  page,
  sortAttr: buildSortAttr(),
  label: activeLabel.value,
});

const fetchContacts = async (page = 1) => {
  clearSelection();
  await store.dispatch('contacts/clearContactFilters');
  await store.dispatch('contacts/get', getCommonFetchParams(page));
  updatePageParam(page);
};

const fetchSavedOrAppliedFilteredContact = async (payload, page = 1) => {
  if (!activeSegmentId.value && !hasAppliedFilters.value) return;
  clearSelection();
  await store.dispatch('contacts/filter', {
    ...getCommonFetchParams(page),
    queryPayload: payload,
  });
  updatePageParam(page);
};

const fetchActiveContacts = async (page = 1) => {
  clearSelection();
  await store.dispatch('contacts/clearContactFilters');
  await store.dispatch('contacts/active', {
    page,
    sortAttr: buildSortAttr(),
  });
  updatePageParam(page);
};

const searchContacts = debounce(async (value, page = 1) => {
  clearSelection();
  await store.dispatch('contacts/clearContactFilters');
  searchValue.value = value;

  if (!value) {
    updatePageParam(page);
    await fetchContacts(page);
    return;
  }

  updatePageParam(page, value);
  await store.dispatch('contacts/search', {
    ...getCommonFetchParams(page),
    search: encodeURIComponent(value),
  });
}, DEBOUNCE_DELAY);

const fetchContactsBasedOnContext = async page => {
  clearSelection();
  updatePageParam(page, searchValue.value);
  if (isFetchingList.value) return;
  if (searchQuery.value) {
    await searchContacts(searchQuery.value, page);
    return;
  }
  // Reset the search value when we change the view
  searchValue.value = '';
  // If we're on the active route, fetch active contacts
  if (isActiveView.value) {
    await fetchActiveContacts(page);
    return;
  }
  // If there are applied filters or active segment with query
  if (
    (hasAppliedFilters.value || activeSegment.value?.query) &&
    !activeLabel.value
  ) {
    const queryPayload =
      activeSegment.value?.query || filterQueryGenerator(appliedFilters.value);
    await fetchSavedOrAppliedFilteredContact(queryPayload, page);
    return;
  }
  // Default case: fetch regular contacts + label
  await fetchContacts(page);
};

const assignLabels = async labels => {
  if (!labels.length || !selectedContactIds.value.length) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContactIds.value,
      labels: { add: labels },
    });
    useAlert(t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS_SUCCESS'));
    clearSelection();
    await fetchContactsBasedOnContext(pageNumber.value);
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const deleteContacts = async () => {
  if (!selectedContactIds.value.length) {
    return;
  }

  isBulkActionLoading.value = true;
  try {
    await BulkActionsAPI.create({
      type: 'Contact',
      ids: selectedContactIds.value,
      action_name: 'delete',
    });
    useAlert(t('CONTACTS_BULK_ACTIONS.DELETE_SUCCESS'));
    clearSelection();
    await fetchContactsBasedOnContext(pageNumber.value);
    bulkDeleteDialogRef.value?.close?.();
  } catch (error) {
    useAlert(t('CONTACTS_BULK_ACTIONS.DELETE_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

// SZ: dropdown de sort + flag pro filter sheet (mesmo da lista de chats)
const showSortMenu = ref(false);
const showFilterSheet = ref(false);
const onSortPick = (sort, order) => {
  showSortMenu.value = false;
  handleSort({ sort, order });
};

// Label do sort ativo (mostrado no pill, igual ao ConversationBasicFilter)
const SORT_LABELS = {
  'name|': 'Nome (A → Z)',
  'name|-': 'Nome (Z → A)',
  'last_activity_at|-': 'Mais recentes',
  'last_activity_at|': 'Mais antigas',
  'created_at|-': 'Adicionados recentemente',
  'created_at|': 'Adicionados há mais tempo',
};
const sortLabel = computed(() => {
  const key = `${sortState.activeSort}|${sortState.activeOrdering}`;
  return SORT_LABELS[key] || 'Mais recentes';
});

// Handlers do filter sheet (placeholder — fase posterior wire com a busca real)
const onContactsFilterApply = filters => {
  showFilterSheet.value = false;
  // TODO: aplicar filtros na busca de contatos. Por enquanto só fecha.
  // eslint-disable-next-line no-console
  console.log('[SZ Contacts] filters applied', filters);
};
const onContactsFilterClear = () => {
  showFilterSheet.value = false;
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    contacts_sort_by: buildSortAttr(),
  });

  if (searchQuery.value) {
    await searchContacts(searchValue.value);
    return;
  }

  if (isActiveView.value) {
    await fetchActiveContacts();
    return;
  }

  await (activeSegmentId.value || hasAppliedFilters.value
    ? fetchSavedOrAppliedFilteredContact(
        activeSegmentId.value
          ? activeSegment.value?.query
          : filterQueryGenerator(appliedFilters.value)
      )
    : fetchContacts());
};

const createContact = async contact => {
  await store.dispatch('contacts/create', contact);
};

watch(
  contacts,
  newContacts => {
    const idsOnPage = newContacts.map(contact => contact.id);
    selectedContactIds.value = selectedContactIds.value.filter(id =>
      idsOnPage.includes(id)
    );
  },
  { deep: true }
);

watch(hasSelection, value => {
  if (!value) {
    bulkDeleteDialogRef.value?.close?.();
  }
});

watch(
  () => uiSettings.value?.contacts_sort_by,
  newSortBy => {
    if (newSortBy) {
      const { sort, order } = parseSortSettings(newSortBy);
      sortState.activeSort = sort;
      sortState.activeOrdering = order;
    }
  },
  { immediate: true }
);

watch(
  [activeLabel, activeSegment, isActiveView],
  () => {
    fetchContactsBasedOnContext(pageNumber.value);
  },
  { deep: true }
);

watch(searchQuery, value => {
  if (isFetchingList.value) return;
  searchValue.value = value || '';
  // Reset the view if there is search query when we click on the sidebar group
  if (value === undefined) {
    if (
      isActiveView.value ||
      activeLabel.value ||
      activeSegment.value ||
      hasAppliedFilters.value
    )
      return;
    fetchContacts();
  }
});

onMounted(async () => {
  if (!activeSegmentId.value) {
    if (searchQuery.value) {
      await searchContacts(searchQuery.value, pageNumber.value);
      return;
    }
    if (isActiveView.value) {
      await fetchActiveContacts(pageNumber.value);
      return;
    }
    await fetchContacts(pageNumber.value);
  } else if (activeSegment.value && activeSegmentId.value) {
    await fetchSavedOrAppliedFilteredContact(
      activeSegment.value.query,
      pageNumber.value
    );
  }

  // SZ: setup do IntersectionObserver pro scroll infinito. O sentinel só
  // existe quando hasMorePages, então watch garante (re)criação dinâmica.
  await nextTick();
  setupInfiniteScroll();
});

const setupInfiniteScroll = () => {
  teardownInfiniteScroll();
  const el = infiniteScrollSentinel.value;
  if (!el) return;
  _ioObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) loadMore();
    });
  }, { root: null, rootMargin: '200px', threshold: 0 });
  _ioObserver.observe(el);
};
const teardownInfiniteScroll = () => {
  if (_ioObserver) { _ioObserver.disconnect(); _ioObserver = null; }
};

// Re-observa quando o sentinel monta/desmonta (após paginação ou troca de view)
watch(infiniteScrollSentinel, async el => {
  await nextTick();
  if (el) setupInfiniteScroll(); else teardownInfiniteScroll();
});

onBeforeUnmount(() => { teardownInfiniteScroll(); });
</script>

<template>
  <!-- SZ Figma: layout 2-col estilo lista de conversas.
       Coluna 1 (sz-contacts-list-col): lista de contatos.
       Coluna 2 (sz-contacts-center-col): empty state grande quando nada selecionado. -->
  <section class="sz-contacts-layout flex w-full h-full min-w-0 bg-n-background">
    <!-- Coluna 1: Lista -->
    <aside class="sz-contacts-list-col">
      <!-- SZ: filter sheet (mesmo da lista de chats) — ancorado ao .sz-contacts-list-col
           via position:absolute. Fica APENAS dentro da coluna de 360px. -->
      <SzContactsFilterSheet
        v-model="showFilterSheet"
        :initial-filters="appliedFilters || []"
        @apply="onContactsFilterApply"
        @clear="onContactsFilterClear"
      />
      <header class="sz-contacts-list-header">
        <div class="sz-contacts-list-title">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          <span>{{ headerTitle || 'Todos os contatos' }}</span>
        </div>
        <div class="sz-contacts-list-actions">
          <!-- SZ: botão de filtro — mesmo ícone do ChatListHeader (3 linhas horizontais).
               TODO fase 2: abrir SzContactsFilterSheet adaptado. -->
          <button
            type="button"
            class="sz-contacts-list-iconbtn sz-contacts-list-iconbtn--filter"
            aria-label="Filtrar"
            @click="showFilterSheet = !showFilterSheet"
          >
            <!-- Mesmo SVG do .sz-fs-funnel. viewBox cortado (1.5 5 17 11) zooma
                 no ícone removendo o espaço vazio nas margens — fica visualmente
                 com o mesmo peso/tamanho que aparece no header do sheet aberto. -->
            <svg width="24" height="24" viewBox="1.5 5 17 11" fill="none" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.625 10.625C15.625 10.7908 15.5592 10.9497 15.4419 11.0669C15.3247 11.1842 15.1658 11.25 15 11.25H5C4.83424 11.25 4.67527 11.1842 4.55806 11.0669C4.44085 10.9497 4.375 10.7908 4.375 10.625C4.375 10.4592 4.44085 10.3003 4.55806 10.1831C4.67527 10.0658 4.83424 10 5 10H15C15.1658 10 15.3247 10.0658 15.4419 10.1831C15.5592 10.3003 15.625 10.4592 15.625 10.625ZM18.125 6.25H1.875C1.70924 6.25 1.55027 6.31585 1.43306 6.43306C1.31585 6.55027 1.25 6.70924 1.25 6.875C1.25 7.04076 1.31585 7.19973 1.43306 7.31694C1.55027 7.43415 1.70924 7.5 1.875 7.5H18.125C18.2908 7.5 18.4497 7.43415 18.5669 7.31694C18.6842 7.19973 18.75 7.04076 18.75 6.875C18.75 6.70924 18.6842 6.55027 18.5669 6.43306C18.4497 6.31585 18.2908 6.25 18.125 6.25ZM11.875 13.75H8.125C7.95924 13.75 7.80027 13.8158 7.68306 13.9331C7.56585 14.0503 7.5 14.2092 7.5 14.375C7.5 14.5408 7.56585 14.6997 7.68306 14.8169C7.80027 14.9342 7.95924 15 8.125 15H11.875C12.0408 15 12.1997 14.9342 12.3169 14.8169C12.4342 14.6997 12.5 14.5408 12.5 14.375C12.5 14.2092 12.4342 14.0503 12.3169 13.9331C12.1997 13.8158 12.0408 13.75 11.875 13.75Z" fill="currentColor"/>
            </svg>
          </button>

          <!-- SZ: botão de ordenação — pill com ícone + label (igual ao
               ConversationBasicFilter da lista de chats). Click abre dropdown. -->
          <div class="sz-contacts-sort-wrap" v-on-clickaway="() => (showSortMenu = false)">
            <div
              class="sz-sortbtn"
              :class="{ 'sz-sortbtn--active': showSortMenu }"
              role="button"
              tabindex="0"
              :aria-label="`Ordenar: ${sortLabel}`"
              @click="showSortMenu = !showSortMenu"
              @keydown.enter.prevent="showSortMenu = !showSortMenu"
              @keydown.space.prevent="showSortMenu = !showSortMenu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21 16-4 4-4-4"/><path d="M17 20V4"/><path d="m3 8 4-4 4 4"/><path d="M7 4v16"/></svg>
              <span class="sz-sortbtn-label">{{ sortLabel }}</span>
            </div>
            <div v-if="showSortMenu" class="sz-contacts-sort-menu">
              <button
                type="button"
                class="sz-contacts-sort-item"
                :class="{ 'sz-contacts-sort-item--active': sortState.activeSort === 'name' && sortState.activeOrdering === '' }"
                @click="onSortPick('name', '')"
              >
                Nome (A → Z)
              </button>
              <button
                type="button"
                class="sz-contacts-sort-item"
                :class="{ 'sz-contacts-sort-item--active': sortState.activeSort === 'name' && sortState.activeOrdering === '-' }"
                @click="onSortPick('name', '-')"
              >
                Nome (Z → A)
              </button>
              <div class="sz-contacts-sort-sep" />
              <button
                type="button"
                class="sz-contacts-sort-item"
                :class="{ 'sz-contacts-sort-item--active': sortState.activeSort === 'last_activity_at' && sortState.activeOrdering === '-' }"
                @click="onSortPick('last_activity_at', '-')"
              >
                Mais recentes
              </button>
              <button
                type="button"
                class="sz-contacts-sort-item"
                :class="{ 'sz-contacts-sort-item--active': sortState.activeSort === 'created_at' && sortState.activeOrdering === '-' }"
                @click="onSortPick('created_at', '-')"
              >
                Adicionados recentemente
              </button>
            </div>
          </div>
        </div>
      </header>

      <div class="sz-contacts-list-scroll">
        <!-- SZ: skeleton no load inicial (substitui o Spinner) — 8 cards pra
             preencher a viewport até a lista real chegar. -->
        <div v-if="isFetchingList" class="sz-contacts-skeleton-list">
          <div v-for="n in 8" :key="n" class="sz-contacts-skeleton-card">
            <span class="sz-cs-avatar" />
            <div class="sz-cs-body">
              <span class="sz-cs-line sz-cs-line--name" />
              <span class="sz-cs-line sz-cs-line--phone" />
              <span class="sz-cs-line sz-cs-line--labels" />
            </div>
          </div>
        </div>
        <template v-else>
          <ContactsBulkActionBar
            v-if="hasSelection"
            :visible-contact-ids="visibleContactIds"
            :selected-contact-ids="selectedContactIds"
            :is-loading="isBulkActionLoading"
            @toggle-all="toggleSelectAll"
            @clear-selection="clearSelection"
            @assign-labels="assignLabels"
            @delete-selected="openBulkDeleteDialog"
          />
          <div v-if="showEmptyText && !showEmptyStateLayout" class="flex items-center justify-center py-10">
            <span class="text-base text-n-slate-11">{{ emptyStateMessage }}</span>
          </div>
          <ContactsList
            v-else-if="hasContacts"
            :contacts="contacts"
            :selected-contact-ids="selectedContactIds"
            @toggle-contact="toggleContactSelection"
          />
          <!-- SZ: skeleton dos cards seguintes enquanto carrega a próxima página -->
          <div v-if="isFetchingMore" class="sz-contacts-skeleton-list">
            <div v-for="n in 3" :key="n" class="sz-contacts-skeleton-card">
              <span class="sz-cs-avatar" />
              <div class="sz-cs-body">
                <span class="sz-cs-line sz-cs-line--name" />
                <span class="sz-cs-line sz-cs-line--phone" />
                <span class="sz-cs-line sz-cs-line--labels" />
              </div>
            </div>
          </div>
          <!-- SZ: sentinel pra IntersectionObserver — quando entra na viewport,
               dispara loadMore() pra carregar a próxima página. -->
          <div
            v-if="hasContacts && hasMorePages"
            ref="infiniteScrollSentinel"
            class="sz-contacts-list-sentinel"
            aria-hidden="true"
          />
        </template>
      </div>
    </aside>

    <!-- SZ: paginação substituída por scroll infinito (sentinel no fim da lista) -->

    <!-- Coluna 2: Empty (sem contato) OU router-view (com :contactId) -->
    <main class="sz-contacts-center-col">
      <router-view v-if="$route.params.contactId" />
      <SzContactsEmpty
        v-else
        @create-contact="createContact"
        @import-contacts="() => {}"
      />
    </main>

    <Dialog
      v-if="selectedCount"
      ref="bulkDeleteDialogRef"
      type="alert"
      :title="bulkDeleteDialogTitle"
      :description="bulkDeleteDialogDescription"
      :confirm-button-label="bulkDeleteDialogConfirmLabel"
      :is-loading="isBulkActionLoading"
      @confirm="deleteContacts"
    />
  </section>
</template>

<style scoped>
.sz-contacts-layout {
  font-family: 'Inter', system-ui, sans-serif;
}

/* Coluna 1: lista de contatos */
.sz-contacts-list-col {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 360px;
  min-width: 360px;
  max-width: 360px;
  height: 100%;
  background: #FFFFFF;
  border-right: 1px solid #E5E7EB;
  box-sizing: border-box;
  /* Ancoragem do SzFilterSheet (que usa position:absolute / left:0 right:0) */
  position: relative;
}
.dark .sz-contacts-list-col {
  background: #1D1E21;
  border-right-color: #2A2C30;
}

/* Header da lista */
.sz-contacts-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  height: 56px;
  border-bottom: 1px solid #E5E7EB;
  box-sizing: border-box;
  flex-shrink: 0;
}
.dark .sz-contacts-list-header {
  border-bottom-color: #2A2C30;
}
.sz-contacts-list-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #000000;
}
.dark .sz-contacts-list-title { color: #FFFFFF; }
.sz-contacts-list-title svg { color: #525252; }
.dark .sz-contacts-list-title svg { color: #9797A0; }

.sz-contacts-list-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
/* SZ: replica EXATAMENTE o .sz-chl-iconbtn da home (transparent + border 1.5px). */
.sz-contacts-list-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1.5px solid #E5E5E5;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  color: #3F3F3F;
  transition: background 0.15s, border-color 0.15s;
  flex-shrink: 0;
  position: relative;
  box-sizing: border-box;
}
.sz-contacts-list-iconbtn:hover { background: rgba(0, 0, 0, 0.03); }
.sz-contacts-list-iconbtn:focus-visible {
  outline: 2px solid #388E3C;
  outline-offset: 1px;
  z-index: 1;
}
.sz-contacts-list-iconbtn--active {
  background: rgba(0, 0, 0, 0.04);
  border-color: #BDBDBD;
}
.dark .sz-contacts-list-iconbtn {
  border-color: #303138;
  color: #FFFFFF;
  background: #1D1E21;
}
.dark .sz-contacts-list-iconbtn:hover { background: #26282B; }
.dark .sz-contacts-list-iconbtn--active { background: #26282B; border-color: #4A4D52; }

.sz-contacts-filter-icon {
  width: 20px;
  height: 20px;
  display: inline-block;
}

/* SZ: pill do botão de sort — igual ao ConversationBasicFilter (Figma) */
.sz-sortbtn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 4px 8px 4px 4px;
  background: #FFFFFF;
  border: 1.5px solid #E5E5E5;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  box-sizing: border-box;
  color: #3F3F3F;
  flex: none;
  transition: background 0.15s, border-color 0.15s;
}
.sz-sortbtn:hover { background: rgba(0, 0, 0, 0.03); }
.sz-sortbtn:focus-visible {
  outline: 2px solid #388E3C;
  outline-offset: 1px;
}
.sz-sortbtn--active { background: rgba(0, 0, 0, 0.04); border-color: #BDBDBD; }
.sz-sortbtn-label {
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  color: inherit;
  white-space: nowrap;
}
.dark .sz-sortbtn {
  background: #1D1E21;
  border-color: #303138;
  color: #FFFFFF;
}
.dark .sz-sortbtn:hover { background: #26282B; }
.dark .sz-sortbtn--active { background: #26282B; border-color: #4A4D52; }
.dark .sz-sortbtn-label { color: #FFFFFF; }

/* SZ: sort dropdown */
.sz-contacts-sort-wrap {
  position: relative;
}
.sz-contacts-sort-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 50;
  min-width: 240px;
  background: #FFFFFF;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.12);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.sz-contacts-sort-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #1F2937;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  transition: background 0.1s ease;
  font-family: inherit;
}
.sz-contacts-sort-item:hover { background: #F3F4F6; }
.sz-contacts-sort-item--active { background: #E6F2E6; color: #2E7D32; }
.sz-contacts-sort-sep {
  height: 1px;
  background: #E5E7EB;
  margin: 4px 6px;
}
.dark .sz-contacts-sort-menu {
  background: #1D1E21;
  border-color: #3D3F43;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5);
}
.dark .sz-contacts-sort-item { color: #E5E7EB; }
.dark .sz-contacts-sort-item:hover { background: #242528; }
.dark .sz-contacts-sort-item--active { background: rgba(89, 170, 89, 0.18); color: #8FD18F; }
.dark .sz-contacts-sort-sep { background: #3D3F43; }

/* Scroll da lista */
.sz-contacts-list-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* Sentinel do IntersectionObserver (scroll infinito) */
.sz-contacts-list-sentinel {
  height: 1px;
}

/* Skeleton dos cards enquanto carrega próxima página */
.sz-contacts-skeleton-list {
  display: flex;
  flex-direction: column;
}
.sz-contacts-skeleton-card {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background: #FFFFFF;
  border-bottom: 1px solid #E5E7EB;
}
.dark .sz-contacts-skeleton-card {
  background: #1D1E21;
  border-bottom-color: #242528;
}
.sz-cs-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #E5E7EB;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}
.sz-cs-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding-top: 4px;
}
.sz-cs-line {
  display: block;
  height: 12px;
  border-radius: 6px;
  background: #E5E7EB;
  position: relative;
  overflow: hidden;
}
.sz-cs-line--name  { width: 60%; }
.sz-cs-line--phone { width: 40%; }
.sz-cs-line--labels { width: 75%; height: 16px; border-radius: 999px; }

.dark .sz-cs-avatar,
.dark .sz-cs-line { background: #2A2C30; }

/* Shimmer animation — gradient passando da esquerda pra direita */
.sz-cs-avatar::after,
.sz-cs-line::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.55) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: translateX(-100%);
  animation: sz-cs-shimmer 1.3s infinite;
}
.dark .sz-cs-avatar::after,
.dark .sz-cs-line::after {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.08) 50%,
    rgba(255, 255, 255, 0) 100%
  );
}
@keyframes sz-cs-shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* Footer com paginação */
.sz-contacts-list-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-top: 1px solid #E5E7EB;
  font-size: 12px;
  color: #525252;
  flex-shrink: 0;
}
.dark .sz-contacts-list-footer {
  border-top-color: #2A2C30;
  color: #9797A0;
}

/* Coluna 2: central grande */
.sz-contacts-center-col {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  display: flex;
}
</style>
